import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, MousePointerClick, RefreshCw, Send } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pennylane/admin/clicks`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const MonthlyReport = () => {
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/monthly`, { headers: auth() });
      if (res.ok) setStats(await res.json());
    })();
  }, []);

  const email = async () => {
    setSending(true); setSent('');
    try {
      const res = await fetch(`${API}/monthly/send`, { method: 'POST', headers: auth() });
      const d = await res.json();
      setSent(d.sent ? `Sent to ${d.to}` : `Not sent: ${d.skipped || d.error || 'unknown reason'}`);
    } finally {
      setSending(false);
    }
  };

  if (!stats) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6" data-testid="clicks-monthly">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            This month · {stats.label}
          </p>
          <p className="text-sm text-slate-700 mt-1" data-testid="monthly-summary">
            <b className="text-slate-900 tabular-nums">{stats.total_clicks}</b> listing views and{' '}
            <b className="text-slate-900 tabular-nums">{stats.total_enquiries}</b> enquiries sent to{' '}
            {stats.by_agent.length} agent{stats.by_agent.length === 1 ? '' : 's'}.
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            A full report is emailed to you automatically at the start of each month, ready to forward.
          </p>
        </div>
        <div className="text-right">
          <Button variant="outline" size="sm" onClick={email} disabled={sending} data-testid="monthly-send">
            <Send size={14} className="mr-2" /> {sending ? 'Sending...' : 'Email me this report'}
          </Button>
          {sent && <p className="text-xs text-slate-500 mt-1" data-testid="monthly-send-result">{sent}</p>}
        </div>
      </div>
    </div>
  );
};

export const PropertyClicksManager = () => {
  const [data, setData] = useState({ total_clicks: 0, by_agent: [], top_listings: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { headers: auth() });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="w-full px-4 sm:px-6 pb-16" data-testid="property-clicks-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Clicks through to agents
          </p>
          <p className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="clicks-total">
            {data.total_clicks}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Counted when a visitor opens an agent&apos;s own listing from a property page.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="clicks-refresh">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <MonthlyReport />

      {loading ? (
        <div className="py-10 text-center text-slate-400">
          <Loader2 size={18} className="animate-spin inline" />
        </div>
      ) : data.total_clicks === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="clicks-empty">
          <MousePointerClick size={20} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600">No click-throughs recorded yet.</p>
          <p className="text-xs text-slate-500 mt-1">
            Counting starts now — figures build up as buyers use the for-sale pages.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[320px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Agent</th>
                  <th className="text-right px-4 py-2 font-medium">Clicks</th>
                </tr>
              </thead>
              <tbody data-testid="clicks-by-agent">
                {data.by_agent.map((row) => (
                  <tr key={row.agent} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium text-slate-900">{row.agent}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{row.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Most-clicked listings</th>
                  <th className="text-right px-4 py-2 font-medium">Clicks</th>
                  <th className="text-right px-4 py-2 font-medium">Last</th>
                </tr>
              </thead>
              <tbody data-testid="clicks-top-listings">
                {data.top_listings.map((row, i) => (
                  <tr key={`${row.address}-${i}`} className="border-t border-slate-100">
                    <td className="px-4 py-2.5">
                      <span className="text-slate-900">{row.address || '(address unavailable)'}</span>
                      <span className="block text-xs text-slate-500">{row.agent}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{row.clicks}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500">{fmt(row.last_click_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyClicksManager;
